/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE BROWSER HALF OF THE PASSKEY CEREMONY.                               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The server verifies; this converts between the JSON the API speaks and the `ArrayBuffer`s the
 * WebAuthn API insists on. That conversion is the entire content of this file, and it is the part
 * everybody gets wrong:
 *
 * ⚠️ **`navigator.credentials` takes and returns BINARY, and JSON has no binary.** The challenge, the
 * credential id and the user handle all cross as `base64url` strings and must be decoded before the
 * call and re-encoded after it. A missed conversion does not throw — it produces a credential the
 * server cannot verify, which reads as "my passkey does not work" with a correct-looking request on
 * both sides.
 *
 * ⚠️ **`base64url`, not `base64`.** They differ in two characters (`+/` versus `-_`) and in padding.
 * A `base64` encoder produces a value the server decodes into *different bytes*, so the signature
 * check fails for a signature that is perfectly valid. `atob`/`btoa` speak plain base64 only.
 *
 * ⚠️ **No `@simplewebauthn/browser`.** The server uses that project's verifier because the
 * cryptography is unforgiving; the browser half is this file's worth of encoding, and a dependency
 * for it would be a bundle and a version to track for forty lines.
 */

/* ── base64url, both ways ─────────────────────────────────────────────────────────────────── */

function toBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  /* A loop rather than `String.fromCharCode(...bytes)`: spreading a large key blows the argument
   * limit, and an attestation object is comfortably large enough. */
  for (const byte of bytes) binary += String.fromCharCode(byte);

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value: string): ArrayBuffer {
  /* ⚠️ Padding restored before `atob`, which rejects an unpadded string in some engines even though
   * base64url omits it by definition. */
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');

  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes.buffer;
}

/* ── what the API sends and expects ───────────────────────────────────────────────────────── */

interface CreationOptions {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: 'public-key'; alg: number }[];
  timeout?: number;
  excludeCredentials?: { id: string; type: 'public-key'; transports?: string[] }[];
  /* ⚠️ The spec's own unions, not `string`. The server only ever sends spec values, and typing
   * these loosely means the object cannot be assigned to `PublicKeyCredentialCreationOptions`
   * without an assertion — which is how a wrong value would reach the browser unnoticed. */
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
}

interface RequestOptions {
  challenge: string;
  rpId?: string;
  timeout?: number;
  userVerification?: UserVerificationRequirement;
  allowCredentials?: { id: string; type: 'public-key'; transports?: string[] }[];
}

/**
 * Whether this browser can do WebAuthn at all.
 *
 * ⚠️ **Also false on an insecure origin**, which is the case that catches people out locally:
 * WebAuthn requires a secure context, and `http://localhost` counts as one while `http://192.168.x.x`
 * does not. So a passkey works on your machine and not from a colleague's — with no error until the
 * call itself fails.
 */
export function passkeysAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as unknown as { credentials?: { create?: unknown; get?: unknown } };
  return (
    window.isSecureContext &&
    typeof window.PublicKeyCredential === 'function' &&
    typeof nav.credentials?.create === 'function' &&
    /* No `?.` here: the line above already narrowed `credentials` to defined. */
    typeof nav.credentials.get === 'function'
  );
}

/** What the authenticator says it speaks — `usb`, `internal`, `hybrid` — or nothing. */
function transportsOf(response: AuthenticatorAttestationResponse): string[] {
  const widened = response as unknown as { getTransports?: () => string[] };
  return typeof widened.getTransports === 'function' ? widened.getTransports() : [];
}

/** Run the registration ceremony and return what the server needs to verify it. */
export async function createPasskey(options: CreationOptions): Promise<unknown> {
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...options,
    challenge: fromBase64Url(options.challenge),
    user: {
      ...options.user,
      /* The user handle the authenticator stores. It is the account id, and it round-trips as
       * bytes. */
      id: fromBase64Url(options.user.id),
    },
    excludeCredentials: options.excludeCredentials?.map((entry) => ({
      ...entry,
      id: fromBase64Url(entry.id),
      transports: entry.transports as AuthenticatorTransport[] | undefined,
    })),
  };

  const credential = (await navigator.credentials.create({
    publicKey,
  })) as PublicKeyCredential | null;

  /* ⚠️ `null` means the person dismissed the prompt, which is not an error and must not be reported
   * as one — a "registration failed" toast for somebody who pressed Escape is a lie. */
  if (credential === null) throw new PasskeyCancelled();

  const response = credential.response as AuthenticatorAttestationResponse;

  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: toBase64Url(response.clientDataJSON),
      attestationObject: toBase64Url(response.attestationObject),
      /* ⚠️ Read through a widened type, not `?.`: the DOM types declare `getTransports()` as
       * always present and older Safari does not ship it — so the optional call the runtime needs is
       * the one the types call unnecessary. Used by the server only to draw an icon. */
      transports: transportsOf(response),
    },
  };
}

/** Run the sign-in ceremony. */
export async function getPasskeyAssertion(options: RequestOptions): Promise<unknown> {
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...options,
    challenge: fromBase64Url(options.challenge),
    allowCredentials: options.allowCredentials?.map((entry) => ({
      ...entry,
      id: fromBase64Url(entry.id),
      transports: entry.transports as AuthenticatorTransport[] | undefined,
    })),
  };

  const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;

  if (credential === null) throw new PasskeyCancelled();

  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: toBase64Url(response.clientDataJSON),
      authenticatorData: toBase64Url(response.authenticatorData),
      signature: toBase64Url(response.signature),
      /* ⚠️ Present only for a discoverable credential, and `null` otherwise — which is why it is
       * conditional rather than always encoded: `toBase64Url(null)` would throw inside a ceremony
       * that had otherwise succeeded. */
      userHandle: response.userHandle === null ? undefined : toBase64Url(response.userHandle),
    },
  };
}

/**
 * The person closed the prompt.
 *
 * ⚠️ Its own type so a caller can stay silent about it. Every other failure deserves a message;
 * this one deserves nothing at all, and telling them apart with a string match on the DOMException
 * name is how "NotAllowedError" ends up in front of a user.
 */
export class PasskeyCancelled extends Error {
  constructor() {
    super('the passkey prompt was dismissed');
    this.name = 'PasskeyCancelled';
  }
}

/**
 * ⚠️ **A dismissed prompt and a genuine refusal share `NotAllowedError`**, which the spec does
 * deliberately: distinguishing them would tell a page whether a credential exists. So a caller
 * cannot know, and the honest treatment is to stay quiet for both — an error message that appears
 * when somebody simply changed their mind trains them to ignore error messages.
 */
export function isCancellation(error: unknown): boolean {
  return (
    error instanceof PasskeyCancelled ||
    (error instanceof DOMException && error.name === 'NotAllowedError')
  );
}
